import {
   EmissionFactorRecord,
   EmissionFactorsApiResponse,
   FeedstockYearlyValue,
   getApiMaterialGrades,
   getApiMaterialTypes,
   getApiSubtypes,
   getEmissionFactorCategories,
   getRemovedYears,
   toEmissionFactorRecord,
   toUpdateRequest,
} from '../types/emission-factor.api';
import { useCallback, useEffect, useState } from 'react';

import APIService from '../../../services/api.service';
import { FeedstockKey, MetricKey } from '../types/emission-factor.constants';
import { ToastService } from '../../../services/toast.service';

const extractRecordFromResponse = (response: EmissionFactorsApiResponse, gradeId: string): EmissionFactorRecord | null => {
   for (const ef of getEmissionFactorCategories(response)) {
      for (const mt of getApiMaterialTypes(ef)) {
         for (const st of getApiSubtypes(mt)) {
            const mg = getApiMaterialGrades(st).find((g) => g.id === gradeId);
            if (mg) {
               return toEmissionFactorRecord({
                  id: mg.id,
                  name: mg.name ?? mg.id,
                  categoryId: ef.id,
                  materialTypeId: mt.id,
                  subtypeId: st.id,
                  feedstocks: mg.feedstocks ?? [],
                  groupId: mg.groupId,
                  groupName: mg.groupName,
               });
            }
         }
      }
   }
   return null;
};

export interface EmissionFactorRecordState {
   currentRecord: EmissionFactorRecord | null;
   originalRecord: EmissionFactorRecord | null;
   loadingRecord: boolean;
   isSaving: boolean;
   saveError: string | null;
   updateYearlyValue: (year: number, feedstock: FeedstockKey, field: MetricKey, value: number | null) => void;
   addYear: (year: number) => boolean;
   removeYear: (year: number) => void;
   saveChanges: (comments?: Record<string, string>, modifyAllInGroup?: boolean) => Promise<void>;
   resetChanges: () => void;
}

interface CacheRef {
   byGrade: Record<string, EmissionFactorsApiResponse>;
}

export function useEmissionFactorRecord(
   selectedGrade: string | null,
   cachedResponsesRef: React.MutableRefObject<CacheRef>,
   onChangesFlushed: () => void
): EmissionFactorRecordState {
   const [currentRecord, setCurrentRecord] = useState<EmissionFactorRecord | null>(null);
   const [originalRecord, setOriginalRecord] = useState<EmissionFactorRecord | null>(null);
   const [loadingRecord, setLoadingRecord] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [saveError, setSaveError] = useState<string | null>(null);

   useEffect(() => {
      if (!selectedGrade) {
         setCurrentRecord(null);
         setOriginalRecord(null);
         return;
      }

      let isCurrent = true;
      const load = async () => {
         setLoadingRecord(true);
         try {
            let response = cachedResponsesRef.current.byGrade[selectedGrade];
            if (!response) {
               response = await APIService.getEmissionFactors({ gradeId: selectedGrade });
               cachedResponsesRef.current.byGrade[selectedGrade] = response;
            }
            if (!isCurrent) return;
            const record = extractRecordFromResponse(response, selectedGrade);
            setCurrentRecord(record);
            setOriginalRecord(record ? structuredClone(record) : null);
         } catch (err) {
            if (isCurrent) {
               setSaveError('Failed to load emission factor data');
               console.error('Error loading record:', err);
            }
         } finally {
            if (isCurrent) setLoadingRecord(false);
         }
      };

      load();
      return () => {
         isCurrent = false;
      };
   }, [selectedGrade, cachedResponsesRef]);

   const updateYearlyValue = useCallback((year: number, feedstock: FeedstockKey, field: MetricKey, value: number | null) => {
      setCurrentRecord((prev) => {
         if (!prev) return prev;
         return {
            ...prev,
            yearlyValues: prev.yearlyValues.map((yv) => {
               if (yv.year !== year) return yv;
               return { ...yv, [feedstock]: { ...yv[feedstock], [field]: value } as FeedstockYearlyValue };
            }),
         };
      });
   }, []);

   const addYear = useCallback(
      (year: number): boolean => {
         if (!currentRecord || currentRecord.availableYears.includes(year)) {
            return false;
         }

         setCurrentRecord((prev) => {
            if (!prev || prev.availableYears.includes(year)) return prev;

            const yearlyValues = [
               ...prev.yearlyValues,
               {
                  year,
                  renewable: { emission: null, share: null },
                  recycled: { emission: null, share: null },
                  nonRenewable: { emission: null, share: null },
               },
            ].sort((a, b) => a.year - b.year);

            return {
               ...prev,
               availableYears: [...prev.availableYears, year].sort((a, b) => a - b),
               yearlyValues,
            };
         });

         return true;
      },
      [currentRecord]
   );

   const removeYear = useCallback((year: number): void => {
      setCurrentRecord((prev) => {
         if (!prev) return prev;
         return {
            ...prev,
            availableYears: prev.availableYears.filter((y) => y !== year),
            yearlyValues: prev.yearlyValues.filter((yv) => yv.year !== year),
         };
      });
   }, []);

   const saveChanges = useCallback(
      async (comments?: Record<string, string>, modifyAllInGroup = false) => {
         if (!currentRecord || !originalRecord || !selectedGrade || isSaving) return;

         setIsSaving(true);
         setSaveError(null);

         try {
            const request = toUpdateRequest(originalRecord, currentRecord, comments, modifyAllInGroup);
            const removedYears = getRemovedYears(originalRecord, currentRecord);

            if (request.length > 0) {
               await APIService.updateEmissionFactors(request);
            }
            if (removedYears.length > 0) {
               await APIService.deleteEmissionFactorYears(selectedGrade, removedYears);
            }

            delete cachedResponsesRef.current.byGrade[selectedGrade];
            const response = await APIService.getEmissionFactors({ gradeId: selectedGrade });
            cachedResponsesRef.current.byGrade[selectedGrade] = response;

            const refreshed = extractRecordFromResponse(response, selectedGrade);
            setCurrentRecord(refreshed);
            setOriginalRecord(refreshed ? structuredClone(refreshed) : null);
            onChangesFlushed();

            const count = request.length + removedYears.length;
            ToastService.success(`${count} emission factor${count !== 1 ? 's' : ''} updated`);
         } catch (err) {
            setSaveError('Failed to save changes');
            console.error('Error saving:', err);
         } finally {
            setIsSaving(false);
         }
      },
      [currentRecord, originalRecord, selectedGrade, isSaving, cachedResponsesRef, onChangesFlushed]
   );

   const resetChanges = useCallback(() => {
      if (originalRecord) {
         setCurrentRecord(structuredClone(originalRecord));
         onChangesFlushed();
      }
   }, [originalRecord, onChangesFlushed]);

   return {
      currentRecord,
      originalRecord,
      loadingRecord,
      isSaving,
      saveError,
      updateYearlyValue,
      addYear,
      removeYear,
      saveChanges,
      resetChanges,
   };
}
